import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cx from "clsx";
import { useField } from "formik";
import { forwardRef, useCallback, useMemo, } from "react";
import FormField from "@ingka/form-field";
import SSRIcon from "@ingka/ssr-icon";
import CrossIcon from "@ingka/ssr-icon/paths/cross";
import Text from "@ingka/text";
import { BaseSelect } from "./Select/BaseSelect";
const Chip = forwardRef(({ text, onRemove, className, disabled, ...props }, ref) => {
    const handleKeyDown = useCallback((event) => {
        if (onRemove === undefined) {
            return;
        }
        if (event.key === "Enter") {
            event.stopPropagation();
            onRemove();
        }
    }, [onRemove]);
    const handleRemoveClick = useCallback((event) => {
        event.stopPropagation();
        onRemove?.();
    }, [onRemove]);
    return (_jsxs("div", { className: cx("pdpp-react-multiSelectContainer", className), ref: ref, "aria-disabled": disabled, ...props, children: [_jsx(Text, { className: "pdpp-react-multiSelectText", tagName: "span", textStyle: "Body.S", children: text }), onRemove ? (_jsx(SSRIcon, { paths: CrossIcon, className: "pdpp-react-multiSelectRemoveIcon", tabIndex: 0, onClick: handleRemoveClick, onKeyDown: handleKeyDown })) : null] }));
});
export function MultiSelect({ value, options, onChange, placeholder = "", label, ...props }) {
    const onRemoveAllClick = useCallback((event) => {
        event.stopPropagation();
        onChange?.([]);
    }, [onChange]);
    const handleRemoveAllKeyDown = useCallback((event) => {
        if (event.key === "Enter") {
            event.stopPropagation();
            onChange?.([]);
        }
    }, [onChange]);
    const onMultiChange = useCallback((nextValue) => onChange?.([...value, nextValue]), [value, onChange]);
    const chips = useMemo(() => value.map((v) => ({
        label: options.find((option) => option.value === v)?.label ?? "",
        onRemove: () => {
            onChange?.(value.filter((v2) => v2 !== v));
        },
    })), [onChange, options, value]);
    const component = useMemo(() => {
        if (value.length === 0) {
            return (_jsx("span", { className: "pdpp-react-multiSelectPlaceholderContainer", children: placeholder }));
        }
        return (_jsxs("div", { className: "pdpp-react-multiSelectSelectInsideContainer", children: [_jsx("div", { className: "pdpp-react-multiSelectChipContainer", children: value.map((v, i) => (_jsx(Chip, { className: "pdpp-react-multiSelectChip", text: chips[i]?.label ?? "", onRemove: chips[i]?.onRemove }, v))) }), _jsx("div", { className: "pdpp-react-multiSelectIconContainer", children: _jsx(SSRIcon, { paths: CrossIcon, className: "pdpp-react-multiSelectIcon", onClick: onRemoveAllClick, onKeyDown: handleRemoveAllKeyDown }) }), _jsx("div", { className: "pdpp-react-multiSelectDivider" })] }));
    }, [chips, onRemoveAllClick, handleRemoveAllKeyDown, placeholder, value]);
    const notSelectedOptions = useMemo(() => options
        .filter((o) => !value.includes(o.value))
        .map(({ label: l, ...rest }) => {
        return { ...rest, component: l };
    }), [options, value]);
    return (_jsxs("div", { children: [label !== undefined ? (_jsx("div", { className: "pdpp-react-multiSelectLabelWrapper", "aria-disabled": props.disabled, children: _jsx(Text, { tagName: "p", textStyle: "Body.S", children: label }) })) : null, _jsx(BaseSelect, { value: "", options: notSelectedOptions, component: component, ...props, onChange: onMultiChange })] }));
}
export function FormikMultiSelect({ name, validate, ...props }) {
    const [field, meta, { setValue, setTouched }] = useField({
        name,
        validate,
    });
    const isValid = !meta.touched || meta.error === undefined;
    const onBlur = useCallback(() => setTouched(true), [setTouched]);
    return (_jsx(FormField, { validation: meta.touched && meta.error !== undefined
            ? {
                id: `error-${field.name}`,
                msg: meta.error,
                type: "error",
            }
            : undefined, shouldValidate: !isValid, valid: isValid, children: _jsx(MultiSelect, { ...field, onChange: setValue, onBlur: onBlur, error: meta.touched ? meta.error : undefined, ...props }) }));
}
//# sourceMappingURL=MultiSelect.js.map